import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { useSuggestionsForNode, useSuggestionsForTaskMeta } from '../../store/suggestions';
import type { Suggestion, Task as TaskModel } from '../../store/types';
import InlineEditPair from './primitives/inline-edit-pair';
import PatchCard from './primitives/patch-card';
import ProseDiff from './primitives/prose-diff';
import { diffField } from './utils/diff-field';
import { pickNoteKey } from './utils/note-key';
type Props = {
  task: TaskModel;
};

// Name/summary refinements. Rendered at the top of the node, next to the name/summary they
// edit, so they lead the canvas order — kept in sync with orderSuggestionsByCanvas.
export const TaskMetaSuggestions = t0 => {
  const $ = _c(7);
  const {
    task
  } = t0;
  const metaSuggestions = useSuggestionsForTaskMeta(task.id);
  let t1;
  if ($[0] !== metaSuggestions || $[1] !== task) {
    let t2;
    if ($[3] !== task) {
      t2 = s => <TaskMetaPatchCard key={s.id} suggestion={s} task={task} />;
      $[3] = task;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = metaSuggestions.map(t2);
    $[0] = metaSuggestions;
    $[1] = task;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

// Whole-node ops (remove / replace). Rendered at the bottom of the node.
export const TaskNodeSuggestions = t0 => {
  const $ = _c(7);
  const {
    task
  } = t0;
  const nodeSuggestions = useSuggestionsForNode(task.id);
  let t1;
  if ($[0] !== nodeSuggestions || $[1] !== task) {
    let t2;
    if ($[3] !== task) {
      t2 = s => <TaskNodePatchCard key={s.id} suggestion={s} task={task} />;
      $[3] = task;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = nodeSuggestions.map(t2);
    $[0] = nodeSuggestions;
    $[1] = task;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
type PatchProps = {
  suggestion: Suggestion;
  task: TaskModel;
};
const TaskMetaPatchCard = t0 => {
  const $0 = _c(30);
  const {
    suggestion,
    task
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.task"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($0[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.suggestion.labels"
    };
    $0[1] = t2;
  } else {
    t2 = $0[1];
  }
  const {
    t: tLabels
  } = useTranslation("agents", t2);
  const op = suggestion.operation;
  if (op.kind !== "update_task_node_meta") {
    return null;
  }
  const isStale = !suggestion.canApply;
  let t3;
  if ($0[2] !== op.name || $0[3] !== task.name) {
    t3 = diffField(task.name, op.name);
    $0[2] = op.name;
    $0[3] = task.name;
    $0[4] = t3;
  } else {
    t3 = $0[4];
  }
  const nameChange = t3;
  let t4;
  if ($0[5] !== op.prompt || $0[6] !== task.prompt) {
    t4 = diffField(task.prompt, op.prompt);
    $0[5] = op.prompt;
    $0[6] = task.prompt;
    $0[7] = t4;
  } else {
    t4 = $0[7];
  }
  const summaryChange = t4;
  const t5 = !!nameChange;
  const t6 = !!summaryChange;
  let t7;
  if ($0[8] !== t5 || $0[9] !== t6) {
    t7 = pickNoteKey({
      refinedName: t5,
      refinedSummary: t6
    }, "refinedNameAndSummary" as const);
    $0[8] = t5;
    $0[9] = t6;
    $0[10] = t7;
  } else {
    t7 = $0[10];
  }
  const noteKey = t7;
  if (!noteKey) {
    return null;
  }
  const t8 = suggestion.id;
  let t9;
  if ($0[11] !== noteKey) {
    t9 = $ => $[noteKey];
    $0[11] = noteKey;
    $0[12] = t9;
  } else {
    t9 = $0[12];
  }
  let t10;
  if ($0[13] !== t || $0[14] !== t9) {
    t10 = t(t9);
    $0[13] = t;
    $0[14] = t9;
    $0[15] = t10;
  } else {
    t10 = $0[15];
  }
  let t11;
  if ($0[16] !== nameChange || $0[17] !== tLabels) {
    t11 = nameChange && <InlineEditPair label={tLabels(_temp)} current={nameChange.from} suggested={nameChange.to} />;
    $0[16] = nameChange;
    $0[17] = tLabels;
    $0[18] = t11;
  } else {
    t11 = $0[18];
  }
  let t12;
  if ($0[19] !== summaryChange || $0[20] !== tLabels) {
    t12 = summaryChange && <div className="flex flex-col gap-0.5"><span className="text-caption-3 text-tertiary">{tLabels(_temp2)}:</span><ProseDiff current={summaryChange.from} suggested={summaryChange.to} /></div>;
    $0[19] = summaryChange;
    $0[20] = tLabels;
    $0[21] = t12;
  } else {
    t12 = $0[21];
  }
  let t13;
  if ($0[22] !== t11 || $0[23] !== t12) {
    t13 = <div className="flex flex-col gap-2">{t11}{t12}</div>;
    $0[22] = t11;
    $0[23] = t12;
    $0[24] = t13;
  } else {
    t13 = $0[24];
  }
  let t14;
  if ($0[25] !== isStale || $0[26] !== suggestion.id || $0[27] !== t10 || $0[28] !== t13) {
    t14 = <PatchCard suggestionId={t8} note={t10} isStale={isStale} showDiffToggle={true}>{t13}</PatchCard>;
    $0[25] = isStale;
    $0[26] = suggestion.id;
    $0[27] = t10;
    $0[28] = t13;
    $0[29] = t14;
  } else {
    t14 = $0[29];
  }
  return t14;
};
const TaskNodePatchCard = t0 => {
  const $ = _c(24);
  const {
    suggestion,
    task
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.task"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const op = suggestion.operation;
  const isStale = !suggestion.canApply;
  if (op.kind === "remove_node") {
    const t2 = suggestion.id;
    let t3;
    if ($[1] !== t) {
      t3 = t(_temp3);
      $[1] = t;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t || $[4] !== task.name) {
      t4 = t(_temp4, {
        name: task.name
      });
      $[3] = t;
      $[4] = task.name;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t4) {
      t5 = <p className="text-caption-3 text-tertiary">{t4}</p>;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== isStale || $[9] !== suggestion.id || $[10] !== t3 || $[11] !== t5) {
      t6 = <PatchCard suggestionId={t2} note={t3} isStale={isStale}>{t5}</PatchCard>;
      $[8] = isStale;
      $[9] = suggestion.id;
      $[10] = t3;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (op.kind === "update_node" && op.node.kind === "task") {
    const t2 = suggestion.id;
    let t3;
    if ($[13] !== t) {
      t3 = t(_temp5);
      $[13] = t;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== t) {
      t4 = t(_temp6);
      $[15] = t;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== t4) {
      t5 = <p className="text-caption-3 text-tertiary">{t4}</p>;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== isStale || $[20] !== suggestion.id || $[21] !== t3 || $[22] !== t5) {
      t6 = <PatchCard suggestionId={t2} note={t3} isStale={isStale}>{t5}</PatchCard>;
      $[19] = isStale;
      $[20] = suggestion.id;
      $[21] = t3;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  return null;
};
function _temp($_0) {
  return $_0.name;
}
function _temp2($_1) {
  return $_1.summary;
}
function _temp3($) {
  return $.remove;
}
function _temp4($_0) {
  return $_0.removeBody;
}
function _temp5($_1) {
  return $_1.replace;
}
function _temp6($_2) {
  return $_2.replaceBody;
}